// REACT
import { useCallback, useMemo } from 'react';
// YARN
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowModes,
  GridTreeDataGroupingCell,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { ClickAwayListener } from '@mui/material';
import { useTranslation } from 'react-i18next';
// SERVICES
import {
  customCultureDataGridTheme,
  CustomToolbarCultures,
} from 'utils/Datagrid/CustomCultureDataGridUtils';
import { toast } from 'react-toastify';
import { CustomToolbar, customToolBarStyle } from 'utils/Datagrid/CustomGenericDataGridUtils';
import getLocaleTextConstants from './CustomDataGridLocaleTextConstants';
import { CustomNoRowsOverlay } from './CustomRotateMatricesDataGridUtils';
// THEMES
import { useCalculatedHeight } from 'utils/tools_functions';
import { createCustomFilterPanel } from './CustomFilterCultureDataGrid';

export default function CustomCultureDataGrid({
  rows,
  columns,
  processRowUpdate,
  loading,
  onRowSelectionModelChange = null,
  rowSelectionModel = {
    organic: { type: 'include', ids: new Set() },
    conventional: { type: 'include', ids: new Set() },
  },
  sortModel,
  columnVisibilityModel,
  setColumnVisibilityModel,
  rowModesModel,
  setRowModesModel,
  toggleVisibilityFilter,
  fromCultures = false,
  isLargeGrid = false,
  isEditCoProduct = false,
  fromParcel = false,
  fromMapping = false,
  isEditableIFT = false,
  isEditInputCost = false,
  isEditMetaculture = false,
  from = false,
  selectedRows = {
    organic: { type: 'include', ids: new Set() },
    conventional: { type: 'include', ids: new Set() },
  },
  handleVisibility,
  handleCulturesShow,
  handleCulturesHide,
  badgeContent = null,
  addCultureModal = false,
  loadMetacultureData = () => {},
  setAddCultureModal = () => {},
  setOpenResetDialog = () => {},
  organicState,
  stackedRequestsRef,
  openOrganicStateDialog,
  customToolbar,
  theme = undefined,
}) {
  const apiRef = useGridApiRef();
  const shouldShowCustomNoRowsOverlay = () => {
    if (fromCultures) {
      return rows.length === 0;
    }
  };

  const handleRowSelectionModelChange = useCallback(
    (newRowSelectionModel) => {
      if (onRowSelectionModelChange != null) {
        const updatedOrganicState =
          localStorage.getItem('isOrganic') === 'true' ? 'organic' : 'conventional';
        onRowSelectionModelChange((prev) => ({
          ...prev,
          [updatedOrganicState]: newRowSelectionModel,
        }));
      }
    },
    [onRowSelectionModelChange],
  );

  const handleRowModesModelChange = useCallback(
    (newModel) => {
      setRowModesModel((prevRowModesModel) => {
        if (Object.keys(newModel).length > 1) {
          for (const key in newModel) {
            if (!newModel[key].hasOwnProperty('fieldToFocus') && newModel[key].mode === 'edit') {
              newModel[key].mode = 'view';
            }
          }
        } else {
          for (const key in newModel) {
            if (newModel[key].mode === 'view') {
              newModel[key].mode = 'edit';
            }
            if (newModel[key].hasOwnProperty('ignoreModifications')) {
              fromParcel && newModel[key].field === 'organicState' && openOrganicStateDialog
                ? (newModel[key].mode = 'edit')
                : (newModel[key].mode = 'view');
            }
          }

          if (Object.keys(prevRowModesModel).length) {
            for (const key in prevRowModesModel) {
              if (prevRowModesModel[key]?.fromScrollBar) {
                newModel[key].mode = 'edit';
                newModel[key].fromScrollBar = false;
              }
              if (prevRowModesModel[key]?.fromScrollBar === false) {
                newModel[key].mode = 'view';
              }
            }
          }
        }

        return newModel;
      });
    },
    [openOrganicStateDialog],
  );

  const { t } = useTranslation();

  const filterConfigs = useMemo(
    () => [
      {
        field: 'cultureType',
        label: t('column.cultureType'),
        operator: 'is',
        options: [
          { value: 'main', label: t('cultureType.main') },
          { value: 'intermediate', label: t('cultureType.intermediate') },
        ],
      },
      // Ajouter un nouveau filtre = ajouter un objet ici
      // {
      //   field: 'cultureSeason',
      //   label: 'Statut',
      //   operator: 'is',
      //   options: [
      //     { value: 'winter', label: 'Hiver' },
      //     { value: 'summer', label: 'Eté' },
      //   ],
      // },
    ],
    [t],
  );

  const CustomFilterPanel = useMemo(() => createCustomFilterPanel(filterConfigs), [filterConfigs]);

  const handleUpdateError = (err) => {
    toast.error(t('errors.editError'));
  };

  const getTogglableColumns = (columns) => {
    return columns
      .filter(
        (column) =>
          column.field !== 'priceN5' &&
          column.field !== 'inputCostN5' &&
          column.field !== 'tfiHerbicide' &&
          column.field !== 'tfiWithoutHerbicide' &&
          column.field !== 'inputCostWithoutNitrogen' &&
          column.field !== 'inputCostWithoutNitrogenN5' &&
          column.field !== '__tree_data_group__' &&
          column.field !== 'metaCultureName',
      )
      .map((column) => column.field);
  };

  const getRowCount = () => {
    return (
      <Box sx={{ marginRight: '16px' }}>
        {t('datagrid.totalRows')} {rows.length}
      </Box>
    );
  };

  const closeRowAfterScrollbarClick = () => {
    for (const key in rowModesModel) {
      if (
        rowModesModel[key]?.mode === 'edit' &&
        rowModesModel[key].hasOwnProperty('fromScrollBar')
      ) {
        apiRef.current?.stopRowEditMode({ id: key });
      }
    }
  };

  const renderParcelGroupingCell = (params) => (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {params.row.parentId?.length > 1 && <span className="parcel-tree-line" />}
      <GridTreeDataGroupingCell {...params} />
    </Box>
  );

  const groupingColDef = {
    width: fromParcel ? 40 : 5,
    valueFormatter: () => '',
    headerName: '',
    align: 'right',
    hideDescendantCount: true,
    ...(fromParcel && { renderCell: renderParcelGroupingCell }),
  };

  const getTreeDataPath = (row) => row.parentId;
  const { ref: containerRef, height } = useCalculatedHeight();

  return (
    <ClickAwayListener onClickAway={closeRowAfterScrollbarClick}>
      <Box
        ref={containerRef}
        sx={{
          height: fromMapping || fromCultures ? height : '70%',
        }}
      >
        <ThemeProvider theme={theme ?? customCultureDataGridTheme}>
          <DataGridPro
            sx={{
              '& .MuiDataGrid-virtualScroller': {
                overflowX: fromCultures ? 'auto !important' : 'hidden',
              },
              '& .MuiDataGrid-overlayWrapper': {
                height: shouldShowCustomNoRowsOverlay() ? '100%' : '',
                margin: shouldShowCustomNoRowsOverlay() ? 'auto' : '',
                '& .MuiDataGrid-overlayWrapperInner': {
                  alignContent: fromCultures && shouldShowCustomNoRowsOverlay() ? 'center' : '',
                },
              },
            }}
            apiRef={apiRef}
            editMode="row"
            onRowEditStop={(params, event) => {
              const target = event.target;
              if (
                target?.classList &&
                Object.keys(target?.classList).length > 0 &&
                Array.from(target?.classList).includes('MuiDataGrid-scrollbar')
              ) {
                const newModel = {
                  [params.id]: {
                    mode: GridRowModes.Edit,
                    fieldToFocus: params.field,
                    fromScrollBar: true,
                  },
                };
                handleRowModesModelChange(newModel);
              }
              if (stackedRequestsRef) {
                stackedRequestsRef.current = [];
              }
            }}
            onRowEditStart={(params) => {
              stackedRequestsRef?.current.push({
                rowId: params.id,
              });
            }}
            onRowClick={closeRowAfterScrollbarClick}
            rowModesModel={rowModesModel}
            onRowModesModelChange={handleRowModesModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
            rows={rows}
            columns={columns}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={handleUpdateError}
            localeText={getLocaleTextConstants()}
            getRowClassName={(params) => {
              if (fromParcel && params.row.parentId.length > 1) {
                return 'MuiDataGrid-ParcelChild--row';
              }
              return params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd';
            }}
            loading={loading}
            disableColumnMenu
            disableColumnResize={true}
            getCellClassName={(params) => {
              if (rowModesModel?.[params.id]) {
                if (params?.field === 'inputCost') {
                  return isEditInputCost
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid-cell--Not-editable';
                }
                if (params?.field === 'tfi') {
                  return isEditableIFT
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid-cell--Not-editable';
                }
                if (params?.field === 'metaCode') {
                  return isEditMetaculture
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid-cell--Not-editable';
                }
                if (params?.isEditable === false) {
                  if (
                    params?.field !== 'inputCost' &&
                    params?.field !== 'tfi' &&
                    params?.field !== 'actions' &&
                    params?.field !== '__check__' &&
                    params?.field !== '__tree_data_group__'
                  ) {
                    return params?.isEditable === false ? 'MuiDataGrid-cell--Not-editable' : '';
                  }
                }
              } else {
                if (params?.field === 'inputCost') {
                  return isEditInputCost
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid--Not-editable';
                }
                if (params?.field === 'tfi') {
                  return isEditableIFT
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid-cell--Not-editable';
                }
                if (from === 'fromCooperative' && params?.field === 'metaCode') {
                  return isEditMetaculture
                    ? 'MuiDataGrid--AllRows-editable'
                    : 'MuiDataGrid-cell--Not-editable';
                }
                if (
                  params?.field !== 'inputCost' &&
                  params?.field !== 'tfi' &&
                  params?.field !== 'actions' &&
                  params?.field !== '__check__' &&
                  params?.field !== 'metaCode'
                ) {
                  if (params?.isEditable === false) {
                    return 'MuiDataGrid--Not-editable';
                  } else {
                    return 'MuiDataGrid--AllRows-editable';
                  }
                }
              }
              return '';
            }}
            getEstimatedRowHeight={() => 80}
            getRowHeight={({ id, model, densityFactor }) => {
              if (
                (isEditCoProduct && model.coProduct === undefined) ||
                (isEditCoProduct && model.coProduct?.length)
              ) {
                return 'auto';
              }
              if (fromMapping) {
                return 52;
              }
              return isLargeGrid ? (model.coProduct?.length > 1 ? 'auto' : 80) : 80;
            }}
            checkboxSelection={fromParcel || fromMapping ? false : true}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            rowSelectionModel={rowSelectionModel[organicState]}
            disableRowSelectionOnClick
            sortModel={sortModel}
            slots={{
              ...(shouldShowCustomNoRowsOverlay() && { noRowsOverlay: CustomNoRowsOverlay }),
              toolbar: customToolbar
                ? () => customToolbar
                : fromCultures
                  ? () => (
                      <CustomToolbarCultures
                        isLargeGrid={isLargeGrid}
                        handleVisibility={handleVisibility}
                        toggleVisibilityFilter={toggleVisibilityFilter}
                        selectedRows={selectedRows?.[organicState]}
                        handleCulturesShow={handleCulturesShow}
                        handleCulturesHide={handleCulturesHide}
                        badgeContent={badgeContent}
                        from={from}
                        addCultureModal={addCultureModal}
                        loadMetacultureData={loadMetacultureData}
                        setAddCultureModal={setAddCultureModal}
                        setOpenResetDialog={setOpenResetDialog}
                        organicState={organicState}
                      />
                    )
                  : CustomToolbar,
              ...(fromParcel && { footerRowCount: getRowCount }),
              ...(fromCultures && { filterPanel: CustomFilterPanel }),
            }}
            slotProps={{
              basePopper: {
                style: customToolBarStyle,
              },
              columnsManagement: {
                getTogglableColumns,
              },
            }}
            showToolbar
            initialState={{
              pinnedColumns: {
                left: fromCultures
                  ? [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'name']
                  : fromParcel
                    ? ['__tree_data_group__', 'name']
                    : [],
                right: fromCultures || fromParcel ? ['actions'] : [],
              },
            }}
            disableColumnReorder={true}
            treeData={fromParcel ? true : false}
            getTreeDataPath={getTreeDataPath}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={-1}
            isCellEditable={(params) => {
              // TFI not editable here because it's a modal and we bypass the datagrid processus
              if (
                params.field === 'inputCost' ||
                params.field === 'tfi' ||
                params.field === 'metaCode'
              ) {
                return false;
              }
              // All columns for a child parcel are not editable
              if (params.row?.parentId?.length > 1) {
                return false;
              }
              if (
                fromParcel &&
                params.field === 'conversionDate' &&
                params.row?.isParent === true
              ) {
                return false;
              }
              return true;
            }}
          />
        </ThemeProvider>
      </Box>
    </ClickAwayListener>
  );
}
